import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Search, 
  MapPin, 
  Phone, 
  User, 
  Settings,
  Clock,
  Navigation
} from 'lucide-react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';

const getLocalDateString = () => {
  const now = new Date();
  return now.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const getLocalTimeZone = () => {
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const offset = -new Date().getTimezoneOffset();
  const sign = offset >= 0 ? '+' : '-';
  const hours = String(Math.floor(Math.abs(offset) / 60)).padStart(2, '0');
  const mins = String(Math.abs(offset) % 60).padStart(2, '0');
  return `${tz} (GMT${sign}${hours}:${mins})`;
};

const FleetManagement = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const ambulances = [
    {
      id: 'AMB-001',
      vehicleNumber: 'KA-01-AB-1234',
      model: 'Mercedes Sprinter',
      year: '2022',
      status: 'active',
      location: 'Downtown Medical Center',
      driver: 'John Smith',
      phone: '+91 98765 43210',
      lastMaintenance: '2024-01-15',
      nextMaintenance: '2024-04-15',
      fuelLevel: 85,
      equipment: ['Defibrillator', 'Oxygen Tank', 'Stretcher', 'First Aid Kit']
    },
    {
      id: 'AMB-002',
      vehicleNumber: 'KA-01-CD-5678',
      model: 'Ford Transit',
      year: '2021',
      status: 'busy',
      location: 'City General Hospital',
      driver: 'Sarah Johnson',
      phone: '+91 98765 43211',
      lastMaintenance: '2024-01-20',
      nextMaintenance: '2024-04-20',
      fuelLevel: 65,
      equipment: ['Defibrillator', 'Oxygen Tank', 'Stretcher', 'Ventilator']
    },
    {
      id: 'AMB-003',
      vehicleNumber: 'KA-01-EF-9012',
      model: 'Toyota Hiace',
      year: '2023',
      status: 'maintenance',
      location: 'Service Center',
      driver: 'Mike Wilson',
      phone: '+91 98765 43212',
      lastMaintenance: '2024-02-01',
      nextMaintenance: '2024-05-01',
      fuelLevel: 45,
      equipment: ['Defibrillator', 'Oxygen Tank', 'Stretcher', 'ECG Machine']
    },
    {
      id: 'AMB-004',
      vehicleNumber: 'KA-01-GH-3456',
      model: 'Mercedes Sprinter',
      year: '2022',
      status: 'active',
      location: 'Suburban Clinic',
      driver: 'Emily Davis',
      phone: '+91 98765 43213',
      lastMaintenance: '2024-01-10',
      nextMaintenance: '2024-04-10',
      fuelLevel: 90,
      equipment: ['Defibrillator', 'Oxygen Tank', 'Stretcher', 'First Aid Kit']
    }
  ];

  const filteredAmbulances = ambulances.filter(ambulance => {
    const matchesSearch = ambulance.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         ambulance.vehicleNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         ambulance.driver.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === 'all' || ambulance.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'status-active';
      case 'busy': return 'status-busy';
      case 'maintenance': return 'status-offline';
      default: return 'status-offline';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'active': return 'Available';
      case 'busy': return 'On Call';
      case 'maintenance': return 'Maintenance';
      default: return 'Offline';
    }
  };

  const handleAddAmbulance = () => {
    toast.success('Ambulance added successfully!');
    setShowAddModal(false);
  };

  return (
    <div className="p-8 bg-neutral-50 min-h-screen">
      <div className="mb-10">
        <h1 className="text-3xl font-bold text-neutral-900 mb-2 tracking-tight">Fleet Management</h1>
        <p className="text-neutral-500">
          Manage ambulance fleet, track status, and monitor maintenance schedules
        </p>
        <div className="flex items-center space-x-4 mt-2 text-sm text-neutral-400">
          <Clock className="w-4 h-4" />
          <span>{currentTime.toLocaleTimeString()}</span>
          <span className="text-neutral-400">|</span>
          <span>{getLocalDateString()}</span>
          <span className="text-neutral-400">|</span>
          <span>{getLocalTimeZone()}</span>
        </div>
      </div>

      {/* Controls */}
      <div className="bg-white rounded-2xl shadow-card border border-neutral-100 p-6 mb-8">
        <div className="flex flex-col lg:flex-row gap-4 items-end">
          {/* Search Bar */}
          <div className="flex-1">
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Search Ambulances
            </label>
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-neutral-300 w-5 h-5" />
              <input
                type="text"
                placeholder="Search by ID, vehicle number, or driver name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input-field pl-12 h-12 text-base"
              />
            </div>
          </div>

          {/* Status Filter */}
          <div className="min-w-[220px] w-full lg:w-auto">
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Filter by Status
            </label>
            <div className="relative">
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="input-field h-12 text-base pr-10 appearance-none bg-neutral-50 focus:bg-white cursor-pointer transition-all"
                style={{ WebkitAppearance: 'none', MozAppearance: 'none', appearance: 'none' }}
              >
                <option value="all">All Status</option>
                <option value="active">Available</option>
                <option value="busy">On Call</option>
                <option value="maintenance">Maintenance</option>
              </select>
              <span className="pointer-events-none absolute right-4 top-1/2 transform -translate-y-1/2 text-neutral-400">
                <svg width="20" height="20" fill="none" viewBox="0 0 20 20"><path d="M6 8l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </span>
            </div>
          </div>

          {/* Add Ambulance Button */}
          <div className="w-full lg:w-auto flex-shrink-0">
            <button
              onClick={() => setShowAddModal(true)}
              className="btn-primary flex items-center space-x-2 h-12 px-6 text-base"
              style={{ minWidth: '160px' }}
            >
              <Plus className="w-5 h-5" />
              <span>Add Ambulance</span>
            </button>
          </div>
        </div>
      </div>

      {/* Ambulance Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {filteredAmbulances.map((ambulance, index) => (
          <motion.div
            key={ambulance.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="card hover:shadow-lg transition-shadow duration-300"
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-xl font-bold text-neutral-900">{ambulance.id}</h3>
                <p className="text-sm text-neutral-500">{ambulance.vehicleNumber}</p>
              </div>
              <span className={getStatusColor(ambulance.status)}>
                {getStatusText(ambulance.status)}
              </span>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <p className="text-sm font-medium text-neutral-500">Model</p>
                <p className="text-sm text-neutral-900">{ambulance.model} ({ambulance.year})</p>
              </div>
              <div>
                <p className="text-sm font-medium text-neutral-500">Fuel Level</p>
                <div className="flex items-center space-x-2">
                  <div className="flex-1 bg-neutral-200 rounded-full h-2">
                    <div 
                      className="bg-primary-400 h-2 rounded-full" 
                      style={{ width: `${ambulance.fuelLevel}%` }}
                    ></div>
                  </div>
                  <span className="text-sm text-neutral-500">{ambulance.fuelLevel}%</span>
                </div>
              </div>
            </div>

            <div className="space-y-3 mb-4">
              <div className="flex items-center space-x-2">
                <User className="w-4 h-4 text-neutral-300" />
                <span className="text-sm text-neutral-900">{ambulance.driver}</span>
              </div>
              <div className="flex items-center space-x-2">
                <Phone className="w-4 h-4 text-neutral-300" />
                <span className="text-sm text-neutral-900">{ambulance.phone}</span>
              </div>
              <div className="flex items-center space-x-2">
                <MapPin className="w-4 h-4 text-neutral-300" />
                <span className="text-sm text-neutral-900">{ambulance.location}</span>
              </div>
            </div>

            <div className="mb-4">
              <p className="text-sm font-medium text-neutral-500 mb-2">Equipment</p>
              <div className="flex flex-wrap gap-1">
                {ambulance.equipment.map((item, idx) => (
                  <span key={idx} className="text-xs bg-primary-50 text-primary-700 px-2 py-1 rounded-full">
                    {item}
                  </span>
                ))}
              </div>
            </div>

            <div className="flex items-center justify-between pt-4 border-t border-neutral-100">
              <div className="text-xs text-neutral-400">
                Next Maintenance: {ambulance.nextMaintenance}
              </div>
              <div className="flex space-x-2">
                <button className="p-2 text-neutral-300 hover:text-primary-500 transition-colors">
                  <Navigation className="w-4 h-4" />
                </button>
                <button className="p-2 text-neutral-300 hover:text-neutral-600 transition-colors">
                  <Settings className="w-4 h-4" />
                </button>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Add Ambulance Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl shadow-xl w-full max-w-md"
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-neutral-100">
              <h2 className="text-xl font-bold text-neutral-900">Add New Ambulance</h2>
              <button
                onClick={() => setShowAddModal(false)}
                className="text-neutral-300 hover:text-neutral-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Ambulance ID
                </label>
                <input 
                  type="text" 
                  className="input-field" 
                  placeholder="AMB-005" 
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Vehicle Number
                </label>
                <input 
                  type="text" 
                  className="input-field" 
                  placeholder="KA-01-XX-0000" 
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Model
                </label>
                <select className="input-field bg-white">
                  <option value="">Select Model</option>
                  <option value="mercedes-sprinter">Mercedes Sprinter</option>
                  <option value="ford-transit">Ford Transit</option>
                  <option value="toyota-hiace">Toyota Hiace</option>
                  <option value="volkswagen-transporter">Volkswagen Transporter</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Year
                </label>
                <input 
                  type="number" 
                  className="input-field" 
                  placeholder="2024" 
                  min="2020"
                  max="2025"
                />
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex space-x-3 p-6 border-t border-neutral-100">
              <button
                onClick={() => setShowAddModal(false)}
                className="flex-1 px-4 py-3 border border-neutral-200 rounded-xl text-neutral-700 hover:bg-neutral-50 transition-colors duration-200 font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleAddAmbulance}
                className="flex-1 btn-primary"
              >
                Add Ambulance
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default FleetManagement; 